import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserCog, Loader2, Plus, Trash2, ShieldCheck } from "lucide-react";
import { api, apiError } from "../lib/api";

const ROLE_LABEL = {
  owner: "Owner", admin: "Admin", gudang_china: "Gudang China", gudang_jakarta: "Gudang Jakarta",
  induk_kedai: "Induk Kedai", agent: "Agen Kedai",
};
const ROLE_OPTIONS = [
  { key: "admin", label: "Admin (ACC member & balas permintaan)" },
  { key: "gudang_china", label: "Gudang China (input resi & marking)" },
  { key: "gudang_jakarta", label: "Gudang Jakarta (input resi sampai Indonesia)" },
  { key: "induk_kedai", label: "Induk Kedai (gudang besar daerah)" },
  { key: "agent", label: "Agen Kedai (kedai kecil)" },
];

export default function StaffManage() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "admin", phone: "", region: "" });
  const [saving, setSaving] = useState(false);

  const load = () => { api.get("/admin/staff").then((r) => setStaff(r.data.staff)).finally(() => setLoading(false)); };
  useEffect(load, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) return toast.error("Lengkapi nama, email, dan password");
    setSaving(true);
    try {
      await api.post("/admin/staff", form);
      toast.success("Akun staf dibuat");
      setForm({ name: "", email: "", password: "", role: "admin", phone: "", region: "" });
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus akun ini?")) return;
    try { await api.delete(`/admin/staff/${id}`); toast.success("Akun dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      <div className="flex items-center gap-3 mb-2"><UserCog className="text-stone-900" size={26} /><h1 className="font-display text-2xl font-bold text-stone-900">Kelola Akun</h1></div>
      <p className="text-stone-500 mb-6 text-sm">Buat akun untuk tim: admin, gudang China, gudang Jakarta, induk kedai, dan agen kedai. Hanya owner yang bisa membuat akun.</p>
      <div className="grid lg:grid-cols-5 gap-6">
        <form onSubmit={submit} className="lg:col-span-2 bg-white rounded-2xl border border-stone-200 p-5 h-fit">
          <h3 className="font-display font-semibold mb-3 flex items-center gap-2"><Plus size={18} />Buat Akun Baru</h3>
          <div className="space-y-3">
            <input data-testid="staff-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Nama lengkap" className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400" />
            <input data-testid="staff-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required placeholder="Email login" className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400" />
            <input data-testid="staff-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required placeholder="Password" className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400" />
            <select data-testid="staff-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm bg-white outline-none focus:border-emerald-400">
              {ROLE_OPTIONS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
            <input data-testid="staff-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="No. HP (opsional)" className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400" />
            {form.role === "induk_kedai" && <input data-testid="staff-region" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="Provinsi/Daerah gudang" className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-400" />}
            <button data-testid="staff-submit" disabled={saving} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-60">{saving && <Loader2 className="animate-spin" size={16} />}Buat Akun</button>
          </div>
        </form>
        <div className="lg:col-span-3">
          {loading ? <div className="py-16 flex justify-center text-emerald-600"><Loader2 className="animate-spin" size={28} /></div> : (
            <div className="space-y-3" data-testid="staff-list">
              {staff.map((s) => (
                <div key={s.id} data-testid={`staff-row-${s.id}`} className="bg-white rounded-2xl border border-stone-200 p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center text-stone-500"><ShieldCheck size={18} /></div>
                  <div className="flex-1">
                    <p className="font-medium text-stone-800 text-sm">{s.name} <span className="text-stone-400">· {s.email}</span></p>
                    <p className="text-xs text-stone-400">{ROLE_LABEL[s.role] || s.role}{s.region ? ` · ${s.region}` : ""}</p>
                  </div>
                  {s.role === "owner" ? <span className="text-amber-600 text-xs font-semibold">Owner</span> : (
                    <button data-testid={`staff-del-${s.id}`} onClick={() => del(s.id)} className="p-2 text-stone-400 hover:text-red-600"><Trash2 size={17} /></button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
